import type { DynamicBlockElement, Key } from '@aline/common';
import {
  arrayEntries,
  buildRenderModel,
  clearAllHeaderFooterCaches,
  CollaborationEditor,
  isCheckboxOptionFieldElement,
  isDynamicBlockElement,
  isSetSelectionOperation,
  NODE_TO_KEY,
  nodesThatNeedCacheRefresh,
  opsIncludeSuggestionOp,
  renderModelCache,
} from '@aline/common';
import type { ApolloClient } from '@apollo/client';
import { makeVar } from '@apollo/client';
import { debounce } from 'debounce';
import { throttle } from 'lodash';
import { equals } from 'ramda';
import type { NodeEntry, Operation } from 'slate';
import { Editor, Path } from 'slate';
import {
  debounceUpdateExpensiveEditorDerivedStateForTabId,
  getEditorTabStateActionsForTabId,
  getEditorTabStateForTabId,
  updateSelectionEditorDerivedStateForTabId,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { documentTypename } from 'src/helpers';
import type { ReactEditor } from 'src/layout-engine';
import { docContentChangedCountVar, suggestionContentChangedCountVar } from 'src/layout-engine';
import { EDITOR_TO_TAB_STATE, EDITOR_TO_UPDATE_CONTEXT } from 'src/layout-engine/utils/weak-maps';

const nonCollapsedDynamicBlock = makeVar<NodeEntry<DynamicBlockElement> | null>(null);

// Track previous render model pages count
const EDITOR_TO_PAGE_COUNT = new WeakMap<Editor, number>();

// Rebuild the render model if the number of pages has changed
// This is needed to ensure correct page numbering generation
const debounceRenderModelRebuild = debounce((editor: Editor) => {
  const currentChildrenLength = editor.renderModel?.children?.length ?? 0;
  const previousChildrenLength = EDITOR_TO_PAGE_COUNT.get(editor) ?? 0;

  if (currentChildrenLength === previousChildrenLength) {
    return;
  }

  EDITOR_TO_PAGE_COUNT.set(editor, currentChildrenLength);
  const updateContext = EDITOR_TO_UPDATE_CONTEXT.get(editor);

  // Clears cached headers and footers for each page
  clearAllHeaderFooterCaches();

  // clears cached elements with page numbers to have correct total pages count
  nodesThatNeedCacheRefresh.forEach(node => {
    renderModelCache.delete(node);
  });

  // clears the arrays of nodes that need cache refresh
  nodesThatNeedCacheRefresh.clear();

  buildRenderModel(editor);

  updateContext?.setSelection(editor.selection);
}, 500);

/**
 * Utility func used to set the collapsed DynamicBlock above the selection, if any.
 * Used to know when we need to rebuild the render model if a user's selection goes inside/out of a collapsed
 * DynamicBlock.
 */
function setCollapsedDynamicBlock(editor: Editor): { collapsedDynamicBlockChanged: boolean } {
  const previousValue = nonCollapsedDynamicBlock();
  const collapsedDynamicBlockEntryAboveSel = Editor.above<DynamicBlockElement>(editor, {
    match: n => isDynamicBlockElement(n) && Queries.nodeIsEmpty(n),
    voids: true,
  });
  if (equals(previousValue, collapsedDynamicBlockEntryAboveSel))
    return { collapsedDynamicBlockChanged: false };

  nonCollapsedDynamicBlock(collapsedDynamicBlockEntryAboveSel);

  return { collapsedDynamicBlockChanged: true };
}

const incDocContentChangedCount = debounce(() => {
  docContentChangedCountVar(docContentChangedCountVar() + 1);
}, 1000);

/**
 * Throttled (leading + trailing) so the suggestions toolbar wakes up on the
 * first redline op of a streaming pass and its count keeps climbing while the
 * stream continues, instead of waiting on the 1s `incDocContentChangedCount`
 * debounce that only fires once the editor is idle.
 */
const incSuggestionContentChangedCount = throttle(
  () => {
    suggestionContentChangedCountVar(suggestionContentChangedCountVar() + 1);
  },
  1000,
  { leading: true, trailing: true }
);

/**
 * `withReact` adds React and DOM specific behaviors to the editor.
 */
export function withReact<T extends Editor>(
  editor: T,
  options: {
    client?: ApolloClient;
    mode?: 'default' | 'tests';
  }
): T & ReactEditor {
  const e: T & ReactEditor = Object.assign(editor, {
    __reactEditor: true as const,
  });

  const { apply } = e;
  const { client, mode = 'default' } = options;

  e.apply = (op: Operation): void => {
    const matches: [Path, Key][] = [];

    switch (op.type) {
      case 'insert_text':
      case 'remove_text':
      case 'set_node': {
        for (const [node, path] of Editor.levels(e, { at: op.path })) {
          const key = Queries.findKey(node);
          matches.push([path, key]);
        }

        break;
      }

      case 'insert_node':
      case 'remove_node':
      case 'merge_node':
      case 'split_node': {
        for (const [node, path] of Editor.levels(e, {
          at: Path.parent(op.path),
        })) {
          const key = Queries.findKey(node);
          matches.push([path, key]);
        }

        break;
      }

      case 'move_node': {
        // TODO
        break;
      }
    }

    apply(op);

    for (const [path, key] of matches) {
      const [node] = Editor.node(e, path);
      NODE_TO_KEY.set(node, key);
    }
  };

  e.onChange = (): void => {
    const isCollabEditor = CollaborationEditor.isCollaborationEditor(e);
    const localChangesInSlateTick =
      !isCollabEditor || CollaborationEditor.hasLocalChangesInSlateTick(e);
    const opsIncludeNonSelOp = e.operations.some(op => !isSetSelectionOperation(op));
    if (opsIncludeNonSelOp && e.selection) {
      e.selection = {
        ...e.selection,
        moveVertWidth: null,
        ...Queries.getNormalizedGridSelectionProps(e, e.selection),
      };
    }

    if (mode === 'default') {
      const updateContext = EDITOR_TO_UPDATE_CONTEXT.get(e);
      const { collapsedDynamicBlockChanged } = setCollapsedDynamicBlock(e);
      if (
        !e.renderModel ||
        !e.operations.length ||
        opsIncludeNonSelOp ||
        collapsedDynamicBlockChanged
      )
        buildRenderModel(e);

      updateContext?.setSelection(e.selection);
      updateContext?.setEditorOnChangeCounter(prev => prev + 1);

      debounceRenderModelRebuild(e);

      const tabId = EDITOR_TO_TAB_STATE.get(e)?.tabId;
      if (tabId) {
        debounceUpdateExpensiveEditorDerivedStateForTabId(tabId, e);
        updateSelectionEditorDerivedStateForTabId(tabId, e);
        if (opsIncludeNonSelOp)
          getEditorTabStateActionsForTabId(tabId).click.setLastMouseDownPoint(null);
      }

      const mouseDownClick = getEditorTabStateForTabId(tabId ?? null)?.click.mouseDownClick;

      if (
        // Only scroll in response to local changes (not remote changes)
        localChangesInSlateTick &&
        // Do not scroll if the user is currently selecting text using the mouse
        !mouseDownClick
      ) {
        ClientTransforms.scrollSelectionIntoView(editor);
      }

      if (opsIncludeNonSelOp && localChangesInSlateTick) {
        const pendingKeys = updateContext?.pendingCheckboxOptionLabelKeys;
        if (pendingKeys) {
          const { operations } = e;
          for (const [opIndex, op] of arrayEntries(operations)) {
            if (op.type !== 'insert_text' && op.type !== 'remove_text') continue;

            const subsequentOperations = operations.slice(opIndex + 1);
            const pathInFinalDoc = subsequentOperations.reduce<Path | null>(
              (path, subsequentOp) => path && Path.transform(path, subsequentOp),
              op.path
            );
            if (!pathInFinalDoc) continue;

            const optionEntry = Editor.above(e, {
              at: pathInFinalDoc,
              match: isCheckboxOptionFieldElement,
              voids: true,
            });

            if (optionEntry) {
              const [optionNode] = optionEntry;
              pendingKeys.add(optionNode.id);
            }
          }
        }
      }
    }

    /**
     * Determining whether the operations contains a non set_selection
     * operation needs to be done BEFORE calling the debounced function not in
     * it. This was fixed from previous behavior where if the last call to the
     * incDocContentChangedCount contains just a set_selection, the entire
     * incDocContentChangedCount function call would be skipped erroneously.
     */
    if (opsIncludeNonSelOp) {
      incDocContentChangedCount();
      if (opsIncludeSuggestionOp(e.operations)) incSuggestionContentChangedCount();

      if (e.documentInfo?.id) {
        const cacheId = client?.cache.identify({
          __typename: documentTypename,
          id: e.documentInfo.id,
        });
        if (cacheId) {
          client?.cache.modify({
            fields: {
              isSavePending: () => true,
            },
            id: cacheId,
          });
        }
      }
    }
  };

  return e;
}
